/**
 * Event Setup
 * Setup all event listeners for search page
 */

import { elements } from '../core/elements.js';
import { state, saveLiveChoice } from '../core/state.js';
import { handleFilterChange } from './filter-handlers.js';
import { getSelectedGender } from '../filters/gender-filter.js';

// Will be injected by index.js
let performSearchAuto = null;
let updateFiltersCount = null;

/**
 * Set dependencies
 */
export function setDependencies(deps) {
  performSearchAuto = deps.performSearchAuto;
  updateFiltersCount = deps.updateFiltersCount;
}

/**
 * Setup all event listeners
 */
export function setupEventListeners() {
  // Note: ZIP search input handlers are removed
  // Search is now triggered only by Search button or URL parameters
  // See search-form.js for form submission handling

  // Live checkbox
  if (elements.liveCheckbox) {
    elements.liveCheckbox.dataset.filter = 'live';
    elements.liveCheckbox.addEventListener('change', handleFilterChange);
  }

  // Age input
  if (elements.ageInput) {
    let ageTimeout;
    elements.ageInput.addEventListener('input', (e) => {
      clearTimeout(ageTimeout);
      ageTimeout = setTimeout(() => {
        state.age = e.target.value.trim();
        if (performSearchAuto) performSearchAuto();
      }, 500);
    });
  }

  // Sort select (camp-sort)
  if (elements.sortSelect) {
    elements.sortSelect.addEventListener('change', (e) => {
      const newSort = e.target.value;

      // Track sort change in Algolia Analytics
      if (window.AlgoliaAnalyticsHandlers && newSort !== state.sort) {
        const queryID = localStorage.getItem('algoliaQueryID');
        if (queryID) {
          window.AlgoliaAnalyticsHandlers.trackSort(newSort, queryID);
        }
      }

      state.sort = newSort;
      if (performSearchAuto) performSearchAuto();
    });
  }

  // Type filter select (type-filter) - Sort by distance/date/popularity
  if (elements.typeFilterSelect) {
    elements.typeFilterSelect.addEventListener('change', (e) => {
      const sortValue = e.target.value;
      console.log('🔄 Type filter changed:', sortValue);

      // Map type-filter values to state.sort values
      // Main template uses value="date-asc", embedded template uses value="date"
      let newSort = state.sort;
      if (sortValue === 'distance') {
        newSort = 'distance';
      } else if (sortValue === 'date' || sortValue === 'date-asc') {
        newSort = 'date-asc';
      } else if (sortValue === 'popularity') {
        newSort = 'popularity';
      }

      // Track sort change in Algolia Analytics (monolith parity)
      if (window.AlgoliaAnalyticsHandlers && newSort !== state.sort) {
        const queryID = localStorage.getItem('algoliaQueryID');
        if (queryID) {
          window.AlgoliaAnalyticsHandlers.trackSort(newSort, queryID);
        }
      }

      state.sort = newSort;
      if (performSearchAuto) performSearchAuto();
    });
  }

  // Mobile filter toggle (open/close filter panel)
  if (elements.mobileFilterToggle) {
    elements.mobileFilterToggle.addEventListener('click', () => {
      if (elements.filterPanel) {
        elements.filterPanel.classList.toggle('open');
      }
    });
  }

  // Close filters (X button in mobile panel)
  if (elements.closeFilters) {
    elements.closeFilters.addEventListener('click', () => {
      if (elements.filterPanel) {
        elements.filterPanel.classList.remove('open');
      }
    });
  }

  // Save filters (mobile SAVE button) — run search and close panel
  if (elements.saveFilters) {
    elements.saveFilters.addEventListener('click', () => {
      if (performSearchAuto) performSearchAuto();
      if (elements.filterPanel) {
        elements.filterPanel.classList.remove('open');
      }
    });
  }

  // Reset filters (mobile panel CLEAR ALL)
  if (elements.resetFilters) {
    elements.resetFilters.addEventListener('click', resetFilters);
  }

  // Reset filters desktop (sidebar CLEAR ALL) — reset and immediately re-search
  if (elements.resetFiltersDesktop) {
    elements.resetFiltersDesktop.addEventListener('click', () => {
      resetFilters();
      if (performSearchAuto) performSearchAuto();
    });
  }

  // Browser back/forward buttons
  window.addEventListener('popstate', async () => {
    console.log('🔙 Browser back/forward detected');
    if (window.AlgoliaSearchModules?.initFromURL) {
      await window.AlgoliaSearchModules.initFromURL(true);
    }
  });
}

/**
 * Reset filters to default
 * Clears all filters and performs new search
 */
export function resetFilters() {
  // CLEAR ALL clears live too (product decision 2026-07-30; the monolith reset
  // it back to the CP default, which left the checkbox checked — confusing).
  // Same semantics as manually unchecking the live checkbox (filter-handlers.js).
  state.live = null;
  state.types = [];
  state.skills = [];
  // Keep state.gender from search form - don't reset it
  state.brand = [];
  state.brandExclude = [];
  state.brandPageBrandSelected = false;
  state.brandPageAllOthers = false;
  // Keep state.tier - don't reset tier parameter when clearing filters
  // Keep state.age - the age comes from the search form, not the sidebar

  // Gender checkboxes: only reset the ones visible for the current form gender
  // (hidden ones keep their state, mirroring the monolith)
  const selectedGender = getSelectedGender();
  if (selectedGender === 'male') {
    state.filterCoed = false;
    state.filterMaleOnly = false;
    // Keep filterFemaleOnly as is (it's hidden)
  } else if (selectedGender === 'female') {
    state.filterCoed = false;
    state.filterFemaleOnly = false;
    // Keep filterMaleOnly as is (it's hidden)
  } else {
    state.filterCoed = false;
    state.filterMaleOnly = false;
    state.filterFemaleOnly = false;
  }

  // Remember the cleared live state — same as a manual uncheck (filter-handlers.js)
  saveLiveChoice(false);

  // Clear non-gender checkboxes in filter panel
  document
    .querySelectorAll(
      '.rd-search-results__filter input[type="checkbox"]:checked:not([name="genderFilter"])'
    )
    .forEach((checkbox) => {
      checkbox.checked = false;
    });

  // Also clear skills checkboxes directly
  elements.skillFilters
    ?.querySelectorAll('input[type="checkbox"]')
    .forEach((cb) => {
      cb.checked = false;
    });

  // Clear only visible gender filter checkboxes
  if (elements.genderFilters) {
    elements.genderFilters
      .querySelectorAll('input[type="checkbox"][name="genderFilter"]:checked')
      .forEach((checkbox) => {
        if (checkbox.offsetParent !== null) checkbox.checked = false;
      });
  }

  if (updateFiltersCount) updateFiltersCount();
}

console.log('📦 Event setup module loaded');
